<script setup lang="ts">
import { computed, h, reactive, ref, watch, type VNodeChild } from 'vue';
import { type CascaderOption } from 'naive-ui';
import {
  PlaybookActionType,
  PlaybookTriggerType,
  type CreatePlaybookTriggerActionResponse,
  type ListPlaybookEvaluationsResponse,
  type PlaybookActionCreateRequest,
  type PlaybookActionUpdate,
} from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import {
  fetchSlackChannels,
  fetchSlackIntegrationStatus,
} from '@/services/integration/slackIntegrationService';
import { fetchJiraIntegrationStatus } from '@/services/integration/jiraIntegrationService';
import { useAuthStore } from '@/stores/auth';
import { enumToSelectOptionsWithLabels } from '@/utils/enumUtils';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import GaiaRemoteSelect, {
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';

type PlaybookEvaluation = ListPlaybookEvaluationsResponse[number];
type PlaybookOptionAction = CreatePlaybookTriggerActionResponse;
type ActionModalMode = 'create' | 'edit';
type ReusableAction = { uid: string; label: string; action: PlaybookOptionAction };

type ActionFormData = {
  action_type: PlaybookActionType;
  name: string;
  instruction: string;
  example_data: string;
  use_web_search: boolean;
  use_organisation_clause_library: boolean;
  use_gaia_clause_library: boolean;
  recipient_email: string;
  recipient_name: string;
  notify_run_initiator: boolean;
  message: string;
  include_original_document: boolean;
  include_redlined_document: boolean;
  include_playbook_summary: boolean;
  include_run_initiator: boolean;
  target_evaluation_uid: string | null;
  channel_id: string | null;
};

const props = withDefaults(
  defineProps<{
    show: boolean;
    mode?: ActionModalMode;
    triggerUid?: string | null;
    evaluationUid?: string | null;
    optionUid?: string | null;
    action?: PlaybookOptionAction | null;
    evaluations?: PlaybookEvaluation[];
    reusableActions?: ReusableAction[];
    /** Which kind of trigger the action hangs off; group triggers can't suggest. */
    triggerScope?: 'evaluation' | 'group';
  }>(),
  {
    mode: 'create',
    triggerUid: null,
    evaluationUid: null,
    optionUid: null,
    action: null,
    evaluations: () => [],
    reusableActions: () => [],
    triggerScope: 'evaluation',
  }
);

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void;
  (event: 'saved', action: PlaybookOptionAction): void;
}>();

const message = useGaiaToast();
const authStore = useAuthStore();
const saving = defineModel<boolean>('saving', { default: false });

const formData = reactive<ActionFormData>(getEmptyFormData());
const reuseSelection = ref<string | null>(null);

const isGroupScope = computed<boolean>(() => props.triggerScope === 'group');

// Slack notification actions require a connected org Slack integration. null
// means the status is unknown (fetch failed) — the option stays enabled then,
// and the backend's 401 on create is the safety net.
const slackConnected = ref<boolean | null>(null);
// Posting is its own scope: a workspace authorized before it existed is still `connected` but
// cannot be posted into, so the action would fail at run time. null is unknown, as above.
const slackCanPushToChannel = ref<boolean | null>(null);

async function refreshSlackStatus(): Promise<void> {
  try {
    const status = await fetchSlackIntegrationStatus();
    slackConnected.value = status.connected;
    slackCanPushToChannel.value = status.can_push_to_channel ?? null;
  } catch {
    slackConnected.value = null;
    slackCanPushToChannel.value = null;
  }
}

// Same gate as Slack, but `connected` alone isn't enough: the backend's
// `jira_integration_usable` also wants the refresh token and the cloud id, and a
// PENDING integration (consent stored, no site chosen yet) has neither. Checking
// only `connected` would leave the option selectable and 401 on create. null
// still means unknown — the option stays enabled and the 401 is the safety net.
const jiraUsable = ref<boolean | null>(null);

async function refreshJiraStatus(): Promise<void> {
  try {
    const status = await fetchJiraIntegrationStatus();
    jiraUsable.value = status.connected && status.has_refresh_token && Boolean(status.cloud_id);
  } catch {
    jiraUsable.value = null;
  }
}

// Channels are searched server-side: an empty-text seed search runs whenever
// the Slack section shows, and typing re-searches (debounced). Each search
// replaces the options wholesale, so the selected channel is kept in the list
// to avoid the select rendering a bare channel id.
/**
 * The field owns the debounce, the options, the loading flag and — through
 * `keepSelected` — the label of the chosen channel, which needed its own
 * `withSelectedSlackChannel` merge here: a channel_id loaded from an existing
 * (or reused) action arrives without a name, and each search replaced the list
 * wholesale.
 */
async function fetchSlackChannelOptions(text: string): Promise<GaiaRemotePage> {
  const channels = await fetchSlackChannels(text);
  return {
    options: channels.map(channel => ({
      label: `#${channel.name}`,
      value: channel.id,
    })),
  };
}

function onSlackChannelError(error: unknown): void {
  message.error(extractErrorDetail(error, 'Failed to load Slack channels.'));
}

/** Suggestion is the usual starting point, but it can't be picked for a group. */
function defaultActionType(): PlaybookActionType {
  return isGroupScope.value ? PlaybookActionType.NOTIFICATION : PlaybookActionType.SUGGESTION;
}

// Evaluation-as-action isn't fully implemented yet, so surface it as a disabled
// "Coming soon" option instead of a selectable one. Signature preparation stays
// visible without its permission, but disabled, so the capability is
// discoverable; Slack and Jira notifications likewise
// stay visible but disabled without a usable integration of their own.
// Suggestions are not supported on group triggers.
const actionTypeOptions = computed(() =>
  enumToSelectOptionsWithLabels(PlaybookActionType, {
    [PlaybookActionType.SLACK_NOTIFICATION]: 'Slack notification',
    // Spelled out rather than left to snakeToTitleCase, which would title-case
    // the second word and read "Jira Notification" next to "Slack notification".
    [PlaybookActionType.JIRA_NOTIFICATION]: 'Jira notification',
  }).map(option => {
    if (option.value === PlaybookActionType.EVALUATION) {
      return { ...option, label: `${option.label} (Coming soon)`, disabled: true };
    }
    if (option.value === PlaybookActionType.SLACK_NOTIFICATION && slackConnected.value === false) {
      return {
        ...option,
        label: `${option.label} (Connect Slack Integrations)`,
        disabled: true,
      };
    }
    if (
      option.value === PlaybookActionType.SLACK_NOTIFICATION &&
      slackConnected.value === true &&
      slackCanPushToChannel.value === false
    ) {
      return {
        ...option,
        label: `${option.label} (Reconnect Slack Integrations)`,
        disabled: true,
      };
    }
    if (option.value === PlaybookActionType.JIRA_NOTIFICATION && jiraUsable.value === false) {
      return {
        ...option,
        label: `${option.label} (Connect Jira Integrations)`,
        disabled: true,
      };
    }
    if (
      option.value === PlaybookActionType.SIGNATURE_PREPARATION &&
      !authStore.userPermissions.can_use_signing
    ) {
      return { ...option, label: `${option.label} (Requires document signing)`, disabled: true };
    }
    if (option.value === PlaybookActionType.SUGGESTION && isGroupScope.value) {
      return { ...option, label: `${option.label} (Not available for groups)`, disabled: true };
    }
    return option;
  })
);

const evaluationOptions = computed(() =>
  props.evaluations.map(evaluation => ({ label: evaluation.name, value: evaluation.uid }))
);

const reuseOptions = computed<CascaderOption[]>(() => {
  const groups = new Map<string, CascaderOption>();
  for (const entry of props.reusableActions) {
    const type = entry.action.action_type;
    // Reusing would otherwise set an action type the select itself disallows.
    // entry.action's action_type is the discriminated-union literal (e.g. 'suggestion'),
    // not the PlaybookActionType enum, so compare against the literal here.
    if (type === 'suggestion' && isGroupScope.value) continue;
    let group = groups.get(type);
    if (!group) {
      const typeOption = actionTypeOptions.value.find(option => option.value === type);
      group = {
        label: typeof typeOption?.label === 'string' ? typeOption.label : type,
        value: type,
        children: [],
      };
      groups.set(type, group);
    }
    group.children?.push({ label: entry.label, value: entry.uid });
  }
  return [...groups.values()];
});

// Show the defining field of the action — prompt or recipient email — on
// hover over a leaf.
function renderReuseLabel(option: CascaderOption): VNodeChild {
  const label = option.label ?? '';
  const entry = props.reusableActions.find(candidate => candidate.uid === option.value);
  if (!entry) return label;
  const action = entry.action as Record<string, unknown>;
  let hint = '';
  switch (action.action_type) {
    case PlaybookActionType.SUGGESTION: {
      const prompt = (action.instruction as string | undefined) ?? '';
      hint = prompt ? `Prompt: ${prompt}` : '';
      break;
    }
    case PlaybookActionType.NOTIFICATION: {
      const email = (action.recipient_email as string | undefined) ?? '';
      hint = email ? `Email: ${email}` : '';
      break;
    }
    // No default
  }
  return h('span', { title: hint || undefined }, label);
}

const modalTitle = computed(() => (props.mode === 'create' ? 'Add action' : 'Edit action'));
const primaryLabel = computed(() => (props.mode === 'create' ? 'Create' : 'Save'));
const isEdit = computed(() => props.mode === 'edit');

watch(
  () => [props.show, props.action, props.mode] as const,
  () => {
    if (!props.show) return;
    resetForm();
    void refreshSlackStatus();
    void refreshJiraStatus();
  },
  { immediate: true }
);

function getEmptyFormData(): ActionFormData {
  return {
    action_type: PlaybookActionType.SUGGESTION,
    name: '',
    instruction: '',
    example_data: '',
    use_web_search: false,
    use_organisation_clause_library: true,
    use_gaia_clause_library: false,
    recipient_email: '',
    recipient_name: '',
    notify_run_initiator: false,
    message: '',
    include_original_document: false,
    include_redlined_document: false,
    include_playbook_summary: false,
    include_run_initiator: false,
    target_evaluation_uid: null,
    channel_id: null,
  };
}

function actionToFormData(action: Record<string, unknown>): ActionFormData {
  return {
    action_type:
      (action.action_type as PlaybookActionType | undefined) ?? PlaybookActionType.SUGGESTION,
    name: (action.name as string | undefined) ?? '',
    instruction: (action.instruction as string | undefined) ?? '',
    example_data: (action.example_data as string | undefined) ?? '',
    use_web_search: (action.use_web_search as boolean | undefined) ?? false,
    use_organisation_clause_library:
      (action.use_organisation_clause_library as boolean | undefined) ?? true,
    use_gaia_clause_library: (action.use_gaia_clause_library as boolean | undefined) ?? false,
    recipient_email: (action.recipient_email as string | undefined) ?? '',
    recipient_name: (action.recipient_name as string | undefined) ?? '',
    notify_run_initiator: (action.notify_run_initiator as boolean | undefined) ?? false,
    message: (action.message as string | undefined) ?? '',
    include_original_document: (action.include_original_document as boolean | undefined) ?? false,
    include_redlined_document: (action.include_redlined_document as boolean | undefined) ?? false,
    include_playbook_summary: (action.include_playbook_summary as boolean | undefined) ?? false,
    include_run_initiator: (action.include_run_initiator as boolean | undefined) ?? false,
    target_evaluation_uid: (action.target_evaluation_uid as string | null | undefined) ?? null,
    channel_id: (action.channel_id as string | null | undefined) ?? null,
  };
}

function resetForm(): void {
  reuseSelection.value = null;
  const action = props.action as Record<string, unknown> | null;
  Object.assign(
    formData,
    action ? actionToFormData(action) : { ...getEmptyFormData(), action_type: defaultActionType() }
  );
}

function applyReuse(uid: string | null): void {
  reuseSelection.value = uid;
  if (!uid) {
    Object.assign(formData, { ...getEmptyFormData(), action_type: defaultActionType() });
    return;
  }
  const entry = props.reusableActions.find(candidate => candidate.uid === uid);
  if (!entry) return;
  Object.assign(formData, actionToFormData(entry.action as Record<string, unknown>));
}

function close(): void {
  emit('update:show', false);
}

function validateForm(): boolean {
  if (!formData.name.trim()) {
    message.error('Name is required');
    return false;
  }
  if (formData.action_type === PlaybookActionType.SUGGESTION && isGroupScope.value) {
    message.error('Suggestion actions are not available for evaluation groups');
    return false;
  }
  if (formData.action_type === PlaybookActionType.SUGGESTION && !formData.instruction.trim()) {
    message.error('Prompt is required for suggestion actions');
    return false;
  }
  if (
    formData.action_type === PlaybookActionType.NOTIFICATION &&
    !formData.notify_run_initiator &&
    !formData.recipient_email.trim()
  ) {
    message.error('Recipient email is required for notification actions');
    return false;
  }
  if (formData.action_type === PlaybookActionType.EVALUATION && !formData.target_evaluation_uid) {
    message.error('A target evaluation is required for evaluation actions');
    return false;
  }
  if (formData.action_type === PlaybookActionType.SLACK_NOTIFICATION && !formData.channel_id) {
    message.error('A channel is required for Slack notification actions');
    return false;
  }
  // The select disables the option, but an action created before the scope went missing keeps
  // its type when reopened — that one can only be caught here.
  if (
    formData.action_type === PlaybookActionType.SLACK_NOTIFICATION &&
    slackCanPushToChannel.value === false
  ) {
    message.error('Reconnect Slack under Integrations before saving a Slack notification action');
    return false;
  }
  return true;
}

function createPayload(): PlaybookActionCreateRequest {
  const base = {
    action_type: formData.action_type,
    name: formData.name.trim(),
  };
  switch (formData.action_type) {
    case PlaybookActionType.SUGGESTION:
      return {
        ...base,
        instruction: formData.instruction.trim(),
        example_data: formData.example_data.trim() || null,
        use_web_search: formData.use_web_search,
        use_organisation_clause_library: formData.use_organisation_clause_library,
        use_gaia_clause_library: formData.use_gaia_clause_library,
      };
    case PlaybookActionType.NOTIFICATION:
      return {
        ...base,
        notify_run_initiator: formData.notify_run_initiator,
        recipient_email: formData.notify_run_initiator ? null : formData.recipient_email.trim(),
        recipient_name: formData.notify_run_initiator
          ? null
          : formData.recipient_name.trim() || null,
        message: formData.message.trim() || null,
        include_original_document: formData.include_original_document,
        include_redlined_document: formData.include_redlined_document,
        include_playbook_summary: formData.include_playbook_summary,
      };
    case PlaybookActionType.SIGNATURE_PREPARATION:
      return {
        ...base,
        message: formData.message.trim() || null,
      };
    case PlaybookActionType.EVALUATION:
      return { ...base, target_evaluation_uid: formData.target_evaluation_uid };
    case PlaybookActionType.SLACK_NOTIFICATION:
      return {
        ...base,
        channel_id: formData.channel_id,
        message: formData.message.trim() || null,
        include_playbook_summary: formData.include_playbook_summary,
        include_run_initiator: formData.include_run_initiator,
      };
    case PlaybookActionType.JIRA_NOTIFICATION:
      // No target field: the issue is resolved per run from where the document came in.
      return {
        ...base,
        message: formData.message.trim() || null,
        include_playbook_summary: formData.include_playbook_summary,
        include_redlined_document: formData.include_redlined_document,
        include_run_initiator: formData.include_run_initiator,
      };
    default:
      return base;
  }
}

function updatePayload(): PlaybookActionUpdate {
  const base = {
    name: formData.name.trim(),
  };
  switch (formData.action_type) {
    case PlaybookActionType.SUGGESTION:
      return {
        ...base,
        instruction: formData.instruction.trim(),
        example_data: formData.example_data.trim() || null,
        use_web_search: formData.use_web_search,
        use_organisation_clause_library: formData.use_organisation_clause_library,
        use_gaia_clause_library: formData.use_gaia_clause_library,
      };
    case PlaybookActionType.NOTIFICATION:
      return {
        ...base,
        notify_run_initiator: formData.notify_run_initiator,
        recipient_email: formData.notify_run_initiator ? null : formData.recipient_email.trim(),
        recipient_name: formData.notify_run_initiator
          ? null
          : formData.recipient_name.trim() || null,
        message: formData.message.trim() || null,
        include_original_document: formData.include_original_document,
        include_redlined_document: formData.include_redlined_document,
        include_playbook_summary: formData.include_playbook_summary,
      };
    case PlaybookActionType.SIGNATURE_PREPARATION:
      return {
        ...base,
        message: formData.message.trim() || null,
      };
    case PlaybookActionType.SLACK_NOTIFICATION:
      return {
        ...base,
        channel_id: formData.channel_id,
        message: formData.message.trim() || null,
        include_playbook_summary: formData.include_playbook_summary,
        include_run_initiator: formData.include_run_initiator,
      };
    case PlaybookActionType.JIRA_NOTIFICATION:
      return {
        ...base,
        message: formData.message.trim() || null,
        include_playbook_summary: formData.include_playbook_summary,
        include_redlined_document: formData.include_redlined_document,
        include_run_initiator: formData.include_run_initiator,
      };
    default:
      return base;
  }
}

async function save(): Promise<void> {
  if (!validateForm()) return;

  saving.value = true;
  try {
    let savedAction: PlaybookOptionAction;
    if (props.mode === 'create') {
      let triggerUid = props.triggerUid;
      if (!triggerUid) {
        if (!props.optionUid || !props.evaluationUid) {
          message.error('Save the option before adding an action.');
          return;
        }
        const trigger = await PlaybooksService.createTrigger(props.evaluationUid, {
          trigger_type: PlaybookTriggerType.OPTION,
          option_uid: props.optionUid,
        });
        triggerUid = trigger.uid;
      }
      savedAction = await PlaybooksService.createAction(triggerUid, createPayload());
      message.success('Action created.');
    } else {
      if (!props.action) return;
      savedAction = await PlaybooksService.updateAction(props.action.uid, updatePayload());
      message.success('Action updated.');
    }

    emit('saved', savedAction);
    close();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save action.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    :title="modalTitle"
    style="width: min(640px, calc(100vw - 32px))"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-form :model="formData" label-placement="top">
      <n-form-item v-if="!isEdit && reuseOptions.length" label="Reuse existing action">
        <n-cascader
          :value="reuseSelection"
          :options="reuseOptions"
          :virtual-scroll="false"
          :theme-overrides="{ columnWidth: '260px' }"
          :menu-props="{ class: 'reuse-action-cascader-menu' }"
          :render-label="renderReuseLabel"
          check-strategy="child"
          clearable
          filterable
          placeholder="Copy fields from an existing action"
          @update:value="applyReuse"
        />
      </n-form-item>

      <n-form-item label="Action type" required>
        <gaia-select
          v-model:value="formData.action_type"
          :options="actionTypeOptions"
          :disabled="isEdit"
          placeholder="Select an action type"
        />
      </n-form-item>

      <n-form-item label="Name" required>
        <gaia-input v-model:value="formData.name" placeholder="Enter a name for this action" />
      </n-form-item>

      <template v-if="formData.action_type === PlaybookActionType.SUGGESTION">
        <section class="suggestion-agent">
          <h3 class="suggestion-agent__title">Suggestion Agent</h3>
        </section>

        <n-form-item label="Prompt" required>
          <gaia-textarea
            v-model:value="formData.instruction"
            :autosize="{ minRows: 4, maxRows: 8 }"
            placeholder="Suggest an amendment to the contract to enhance this clause."
          />
        </n-form-item>

        <n-form-item label="Example Data (Optional)">
          <n-space vertical>
            <n-text depth="3">
              Add example data for the AI, e.g. a clause or a section of the contract.
            </n-text>
            <gaia-textarea
              v-model:value="formData.example_data"
              :autosize="{ minRows: 3, maxRows: 8 }"
              placeholder="Enter example suggestion"
            />
          </n-space>
        </n-form-item>

        <n-form-item label="Available Tools">
          <n-space vertical>
            <n-text depth="3">
              Select which tools the suggestion agent can use to enhance its suggestions.
            </n-text>
            <gaia-checkbox v-model:checked="formData.use_organisation_clause_library">
              Organisation Clause Library
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.use_gaia_clause_library">
              Gaia Clause Library
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.use_web_search">Web Search</gaia-checkbox>
          </n-space>
        </n-form-item>
      </template>

      <template v-else-if="formData.action_type === PlaybookActionType.NOTIFICATION">
        <n-form-item label="Recipient" required>
          <n-space vertical :size="8" style="width: 100%">
            <n-space align="center" :size="10">
              <gaia-switch v-model:value="formData.notify_run_initiator" size="small" />
              <n-text depth="3">Notify the person who started the run</n-text>
            </n-space>
            <gaia-input
              v-if="!formData.notify_run_initiator"
              v-model:value="formData.recipient_email"
              placeholder="name@example.com"
            />
          </n-space>
        </n-form-item>
        <n-form-item v-if="!formData.notify_run_initiator" label="Recipient name">
          <gaia-input
            v-model:value="formData.recipient_name"
            placeholder="Optional recipient name"
          />
        </n-form-item>
        <n-form-item label="Message">
          <gaia-textarea
            v-model:value="formData.message"
            :autosize="{ minRows: 3, maxRows: 8 }"
            placeholder="Optional notification message"
          />
        </n-form-item>
        <n-form-item label="Attachments">
          <n-space vertical>
            <gaia-checkbox v-model:checked="formData.include_original_document">
              Include Original Document
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.include_redlined_document">
              Include Redlined Document
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.include_playbook_summary">
              Include Summary
            </gaia-checkbox>
          </n-space>
        </n-form-item>
      </template>

      <template v-else-if="formData.action_type === PlaybookActionType.SIGNATURE_PREPARATION">
        <n-form-item label="Message">
          <gaia-textarea
            v-model:value="formData.message"
            :autosize="{ minRows: 3, maxRows: 8 }"
            placeholder="Optional message for the signature request"
          />
        </n-form-item>
      </template>

      <template v-else-if="formData.action_type === PlaybookActionType.EVALUATION">
        <n-form-item label="Target evaluation" required>
          <gaia-select
            v-model:value="formData.target_evaluation_uid"
            :options="evaluationOptions"
            :disabled="isEdit"
            placeholder="Select an evaluation to trigger"
          />
        </n-form-item>
      </template>

      <template v-else-if="formData.action_type === PlaybookActionType.SLACK_NOTIFICATION">
        <n-form-item label="Channel" required>
          <!-- prime-on-mount, not a watch on action_type: this field only
               renders once the action IS a Slack notification, so mounting is
               exactly the moment the old watch fired. -->
          <gaia-remote-select
            v-model:value="formData.channel_id"
            clearable
            prime-on-mount
            placeholder="Select a Slack channel"
            :debounce="400"
            :fetch="fetchSlackChannelOptions"
            @error="onSlackChannelError"
          />
        </n-form-item>
        <n-form-item label="Message">
          <gaia-textarea
            v-model:value="formData.message"
            :autosize="{ minRows: 3, maxRows: 8 }"
            placeholder="Optional notification message"
          />
        </n-form-item>
        <n-form-item label="Options">
          <n-space vertical>
            <gaia-checkbox v-model:checked="formData.include_playbook_summary">
              Include Summary
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.include_run_initiator">
              Include Run Initiator
            </gaia-checkbox>
          </n-space>
        </n-form-item>
        <n-text depth="3">
          Post a notification to the selected Slack channel when this option is chosen.
          Notifications from a run are combined into one Slack message posted after the run
          finishes.
        </n-text>
      </template>

      <template v-else-if="formData.action_type === PlaybookActionType.JIRA_NOTIFICATION">
        <!-- No target picker, unlike Slack's channel: the issue is the one this run's
             document arrived on, resolved per run rather than configured here. -->
        <n-form-item label="Message">
          <gaia-textarea
            v-model:value="formData.message"
            :autosize="{ minRows: 3, maxRows: 8 }"
            placeholder="Optional comment message"
          />
        </n-form-item>
        <n-form-item label="Options">
          <n-flex vertical :size="8" :wrap="false">
            <gaia-checkbox v-model:checked="formData.include_playbook_summary">
              Include Summary
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.include_redlined_document">
              Include Redlined Document
            </gaia-checkbox>
            <gaia-checkbox v-model:checked="formData.include_run_initiator">
              Include Run Initiator
            </gaia-checkbox>
          </n-flex>
        </n-form-item>
        <gaia-text tag="p" tone="muted" style="margin: 0">
          Posts one comment on the Jira issue this run's document came from, after the run finishes.
          Runs not started from a Jira issue will skip it. The redlined document is uploaded as an
          attachment on that issue.
        </gaia-text>
      </template>
    </n-form>

    <template #footer>
      <n-space justify="end">
        <n-button @click="close">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="save">
          {{ primaryLabel }}
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>

<style scoped>
.suggestion-agent {
  margin-bottom: 12px;
}

.suggestion-agent__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}
</style>

<style>
/* Cascader menu is teleported to body, so this override is intentionally unscoped
   and namespaced by the menu class. Let each column size to its content (up to the
   themed max height) instead of the default fixed height. */
.reuse-action-cascader-menu .n-cascader-submenu {
  height: auto;
  max-height: var(--n-menu-height);
}
</style>
